import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import getLPTheme from "./getLPTheme";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import axios from "axios";
import { fetchUser } from "../apis/UserAPI";
import PasswordStrengthBar from "react-password-strength-bar";

import { startRegistration } from "@simplewebauthn/browser";

// Helper function to get auth token from cookies
const getAuthToken = () => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("_auth="))
    ?.split("=")[1];
  return token;
};

export default function Profile() {
  const [mode, setMode] = useState("light");
  const LPtheme = createTheme(getLPTheme(mode));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeys, setPasskeys] = useState([]);



  const fetchPasskeys = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
          const resp = await axios.get(
              process.env.REACT_APP_API_URL + "/api/passkey/list",
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setPasskeys(resp.data.passkeys || []);
      } catch (e) {
          console.error("Failed to fetch passkeys", e);
      }
  };

  const deletePasskey = async (credentialId) => {
      if (!window.confirm("Are you sure you want to delete this passkey?")) return;
      
      const token = getAuthToken();
      try {
          await axios.delete(
              process.env.REACT_APP_API_URL + `/api/passkey/${encodeURIComponent(credentialId)}`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setPasskeys(passkeys.filter(pk => pk.credential_id !== credentialId));
          setSuccess("Passkey deleted.");
          setShowAlert(true);
      } catch (e) {
          setError("Failed to delete passkey.");
          setShowAlert(true);
      }
  };


  const initialFormValues = useRef({});
  const authUser = useAuthUser();

  useEffect(() => {
    if (authUser && authUser.uid) {
        fetchPasskeys();
    }
  }, [authUser]);

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const registerPasskey = async () => {
    setPasskeyLoading(true);
    setError("");
    setSuccess("");
    setShowAlert(false);
    
    try {
        const token = getAuthToken();
        if (!token) throw new Error("Not authenticated");

        // 1. Get options from server
        const resp = await axios.post(
            process.env.REACT_APP_API_URL + "/api/passkey/register/start", 
            {},
            { 
                 headers: { Authorization: `Bearer ${token}` },
                 withCredentials: true 
            }
        );

        const { options, challenge_token } = resp.data;

        // 2. Pass options to browser authenticator
        let attResp;
        try {
          attResp = await startRegistration(options);
        } catch (e) {
          // If user cancels or times out, handle gracefully
          if (e.name === 'NotAllowedError') {
              throw new Error("Registration cancelled or timed out.");
          }
          throw e;
        }

        // 3. Send response to server
        const verificationResp = await axios.post(
            process.env.REACT_APP_API_URL + "/api/passkey/register/finish",
            {
               credential: attResp,
               challenge_token: challenge_token
            },
            { 
                 headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                 withCredentials: true 
            }
        );

        if (verificationResp.data.verified) {
            setSuccess("Passkey registered successfully!");
            setShowAlert(true);
            fetchPasskeys(); // Refresh list
        } else {
            setError("Passkey verification failed.");
            setShowAlert(true);
        }

    } catch (e) {
        console.error(e);
        setError(e.message || "Failed to register passkey.");
        setShowAlert(true);
    } finally {
        setPasskeyLoading(false);
    }
  };

  const resetPasswordFields = () => {
    setPassword("");
    setConfirmPassword("");
    setIsChangingPassword(false);
  };

  const isPasswordMatch = () => {
    if (!isChangingPassword) return true;
    return !password || !confirmPassword || password === confirmPassword;
  };

  const handleFormChange = (event) => {
    const currentValues = {
      name: event.target.form.name.value,
      email: event.target.form.email.value,
    };

    setHasChanges(
      currentValues.name !== initialFormValues.current.name ||
        currentValues.email !== initialFormValues.current.email ||
        (isChangingPassword && password)
    );
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    handleFormChange(event);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    handleFormChange(event);
  };

  const handleInputChange = (event) => {
    handleFormChange(event);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setLoading(true);
    setError("");
    setSuccess("");
    setShowAlert(false);

    const token = getAuthToken(); // Get the token
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      setShowAlert(true);
      setLoading(false);
      return;
    }

    try {
      const requestData = {
        uid: authUser.uid,
        name: formData.get("name"),
        contact: formData.get("email"),
        type_id: userData.user_type,
        tid: userData.tid,
        change_pass: isChangingPassword ? "True" : "False",
      };

      if (isChangingPassword) {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          setShowAlert(true);
          setLoading(false);
          return;
        }
        requestData.password = password;
      }

      await axios.post(
        process.env.REACT_APP_API_URL + "/api/edit-user",
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true, // Keep if necessary for your setup
        }
      );

      setSuccess("Profile updated successfully!");
      setShowAlert(true);
      setEditing(false);
      resetPasswordFields();
      setHasChanges(false);

      const currentToken = getAuthToken(); // Get token again, or pass it down if already available
      if (!currentToken) {
        setError("Session expired. Please log in again to refresh data.");
        setShowAlert(true);
        // Potentially stop further execution or handle redirect
        return;
      }
      const updatedData = await fetchUser(authUser.uid, currentToken);
      setUserData(updatedData[0]);
      initialFormValues.current = {
        name: updatedData[0].name,
        email: updatedData[0].contact,
      };
    } catch (error) {
      setError("Failed to update profile");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setError("Authentication token not found. Please log in again.");
          setShowAlert(true);
          setInitialLoading(false);
          return;
        }
        const data = await fetchUser(authUser.uid, token);
        if (data && data.length > 0) {
          setUserData(data[0]);
          initialFormValues.current = {
            name: data[0].name,
            email: data[0].contact,
          };
        } else {
          throw new Error("User data not found or invalid format.");
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        setError(error.message || "Failed to load user data");
        setShowAlert(true);
      } finally {
        setInitialLoading(false);
      }
    };

    if (authUser?.uid) {
      loadUserData();
    } else {
      setError("User not authenticated.");
      setShowAlert(true);
      setInitialLoading(false);
    }
  }, [authUser?.uid]);

  if (initialLoading) {
    return (
      <ThemeProvider theme={LPtheme}>
        <CssBaseline />
        <AppAppBar
          mode={mode}
          toggleColorMode={toggleColorMode}
        />
        <Box
          sx={{
            backgroundImage:
              "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
            backgroundSize: "100% 200px",
            backgroundRepeat: "no-repeat",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress />
          <Typography
            variant='h6'
            sx={{ mt: 2 }}
          >
            Loading profile...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box
        sx={{
          backgroundImage:
            "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
          backgroundSize: "100% 200px",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          px: 3,
        }}
      >
        <Container maxWidth='xl'>
          <Grid
            container
            spacing={4}
            justifyContent='center'
          >
            {/* Profile Section */}
            <Grid
              item
              xs={12}
              md={8}
              lg={6}
            >
              {/* Profile Paper */}
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      m: 1,
                      bgcolor: "primary.main",
                      width: 56,
                      height: 56,
                    }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography
                    component='h1'
                    variant='h5'
                    sx={{ mb: 3 }}
                  >
                    Profile
                  </Typography>

                  {userData && (
                    <Box
                      component='form'
                      onSubmit={handleSubmit}
                      noValidate
                      sx={{ width: "100%" }}
                    >
                      <TextField
                        margin='normal'
                        required
                        fullWidth
                        id='name'
                        label='Name'
                        name='name'
                        autoComplete='name'
                        defaultValue={userData.name}
                        disabled={!editing || loading}
                        onChange={handleInputChange}
                      />
                      <TextField
                        margin='normal'
                        required
                        fullWidth
                        id='email'
                        label='Email Address'
                        name='email'
                        autoComplete='email'
                        defaultValue={userData.contact}
                        disabled={!editing || loading}
                        onChange={handleInputChange}
                      />

                      {editing && (
                        <>
                          <Divider sx={{ my: 3 }} />
                          <Typography
                            variant='h6'
                            sx={{ mb: 2 }}
                          >
                            Change Password
                          </Typography>

                          {!isChangingPassword ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                fullWidth
                                variant='outlined'
                                onClick={() => setIsChangingPassword(true)}
                                disabled={loading || passkeyLoading}
                                >
                                Change Password
                                </Button>
                                <Button
                                fullWidth
                                variant='outlined'
                                color="secondary"
                                onClick={registerPasskey}
                                disabled={loading || passkeyLoading}
                                >
                                {passkeyLoading ? (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Registering...
                                    </Box>
                                ) : (
                                    "Register Passkey"
                                )}
                                </Button>

                                <Box sx={{ mt: 4, width: '100%', maxWidth: 400 }}>
                                  <Typography variant="h6" gutterBottom>
                                    Your Passkeys
                                  </Typography>
                                  {passkeys.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                      No passkeys registered.
                                    </Typography>
                                  ) : (
                                    <List dense>
                                      {passkeys.map((pk, index) => (
                                        <ListItem key={pk.credential_id}
                                          secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => deletePasskey(pk.credential_id)}>
                                              <DeleteIcon />
                                            </IconButton>
                                          }
                                        >
                                          <ListItemText
                                            primary={`Passkey ${index + 1}`}
                                            secondary={`Added on ${pk.created_at}`}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </Box>
                            </Box>
                          ) : (
                            <>
                              <TextField
                                margin='normal'
                                required
                                fullWidth
                                name='password'
                                label='New Password'
                                type='password'
                                id='password'
                                value={password}
                                onChange={handlePasswordChange}
                                disabled={loading}
                              />
                              <PasswordStrengthBar password={password} />
                              <TextField
                                margin='normal'
                                required
                                fullWidth
                                name='confirmPassword'
                                label='Confirm New Password'
                                type='password'
                                id='confirmPassword'
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                error={!isPasswordMatch()}
                                helperText={
                                  !isPasswordMatch() && "Passwords do not match"
                                }
                                disabled={loading}
                              />
                            </>
                          )}

                          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                            <Button
                              fullWidth
                              variant='outlined'
                              onClick={() => {
                                setEditing(false);
                                resetPasswordFields();
                                setHasChanges(false);
                              }}
                              disabled={loading}
                            >
                              Cancel
                            </Button>
                            <Button
                              type='submit'
                              fullWidth
                              variant='contained'
                              disabled={
                                loading ||
                                !hasChanges ||
                                (isChangingPassword &&
                                  (!password ||
                                    !confirmPassword ||
                                    !isPasswordMatch()))
                              }
                            >
                              {loading ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <CircularProgress
                                    size={20}
                                    sx={{ mr: 1 }}
                                  />
                                  Saving...
                                </Box>
                              ) : (
                                "Save"
                              )}
                            </Button>
                          </Box>
                        </>
                      )}

                      {!editing && (
                        <Button
                          fullWidth
                          variant='contained'
                          onClick={() => setEditing(true)}
                          sx={{ mt: 2 }}
                          disabled={loading}
                        >
                          Edit Profile
                        </Button>
                      )}
                    </Box>
                  )}

                  {(error || success) && (
                    <Box sx={{ mt: 2, width: "100%" }}>
                      {error && (
                        <Alert
                          variant='outlined'
                          severity='error'
                          sx={{
                            visibility: showAlert ? "visible" : "hidden",
                          }}
                        >
                          {error}
                        </Alert>
                      )}

                      {success && (
                        <Alert
                          variant='outlined'
                          severity='success'
                          sx={{
                            visibility: showAlert ? "visible" : "hidden",
                          }}
                        >
                          {success}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
